import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthContext";
import { EsqueletoDashboard } from "../../components/Esqueleto";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Tarjeta } from "../../components/Tarjeta";
import { api } from "../../lib/apiClient";
import { COLOR_POR_NIVEL_CARGA, resolverColorSemantico } from "../../lib/colorSemantico";
import { calcularRangoPeriodo } from "../../lib/periodo";
import { calcularNivelCarga, type NivelCarga } from "../mi-trabajo/colaTrabajo";
import type { CargaTrabajoAbogado } from "../mi-trabajo/CargaEquipoTabla";
import { formatearPorMoneda } from "./ComunesDashboard";
import type { FilaMoneda, Severidad } from "./ComunesDashboard";
import { FranjaEstado } from "./FranjaEstado";
import { BarraObjetivo, listaObjetivos } from "./PanelObjetivos";
import type { DatosObjetivos } from "./PanelObjetivos";

// ============================================================================
// Vista General del Dashboard — pantalla-termómetro ("¿cómo está el estudio?
// + ¿dónde debo intervenir?"), aprobada tras 2 rondas de auditoría UX. Debe
// entrar completa en 1366×768 sin scroll vertical: máximo 10 KPIs, máximo 3
// alertas. Nunca un lugar de trabajo — "¿qué hago?" es exclusivo de Mi
// Trabajo, por eso ninguna fila/alerta de acá tiene un botón de acción
// propio; el único botón de la pantalla ("Ver seguimiento operativo") sólo
// navega a /centro-operaciones.
//
// Decisión de arquitectura (impacto en rendimiento): los datos que necesita
// esta vista están repartidos entre 5 endpoints YA EXISTENTES — no se creó
// ningún endpoint nuevo. Se combinan acá en el frontend con 5 fetches fijos
// en paralelo (nunca por fila, no es N+1):
//   - GET /dashboard/ceo              → Franja de Estado + expedientes
//                                        activos + facturación/resultado/
//                                        honorarios pendientes (Negocio).
//   - GET /dashboard/ejecutivo        → saldo de Cajas + Objetivos (Negocio).
//   - GET /dashboard/judicial         → tiempo promedio de resolución
//                                        (Operación) + Por Materia (Estrategia).
//   - GET /mi-trabajo/carga-trabajo   → Carga del equipo (Operación) — sólo
//                                        con expedientes.ver_todos.
//   - GET /dashboard/unidades-negocio → Unidad más rentable (Estrategia) —
//                                        sólo con economia.ver.
// Los primeros 3 ya eran necesarios para el resto del Dashboard (las vistas
// Judicial/Financiero siguen usándolos); los últimos 2 son los que empujan
// el total de 3 a 5 fetches — no había forma de obtener "Carga del equipo"
// ni "Unidad más rentable" sin ellos, sin inventar un endpoint nuevo o
// duplicar SQL que ya vive en esas rutas.
// ============================================================================

interface AlertaCritica {
  tipo: string;
  etiqueta: string;
  cantidad: number;
  severidad: Severidad;
}

interface DatosCeo {
  expedientesActivos: number;
  resultadoMes: FilaMoneda[] | null;
  facturacionMes: FilaMoneda[] | null;
  facturacionMesAnterior: FilaMoneda[] | null;
  honorariosPendientes: FilaMoneda[] | null;
  centroOperaciones: { alertasCriticas: AlertaCritica[] };
}

interface DatosEjecutivoParcial {
  finanzas: { saldoCajas: { id: string; nombre: string; moneda: string; saldo: number }[] } | null;
  objetivos: DatosObjetivos;
}

interface DatosJudicialParcial {
  tiempoPromedioResolucionDias: number | null;
  graficos: { porMateria: { etiqueta: string; total: number }[] };
}

interface UnidadNegocio {
  id: string;
  nombre: string;
  resultado: FilaMoneda[];
}

// ---------------------------------------------------------------------------
// Helpers puros de presentación — ninguno recalcula una regla de negocio:
// todos leen números que el backend ya entregó calculados y sólo deciden
// cómo mostrarlos (comparar, ordenar, formatear). Ver comentario de cada uno
// para la fuente exacta.

// "Facturación del mes vs. mes anterior" (spec §3 Negocio) — variación %
// simple por moneda entre dos totales que el backend ya sumó
// (facturacionMes / facturacionMesAnterior de GET /dashboard/ceo).
function compararFacturacion(actual: FilaMoneda[] | null, anterior: FilaMoneda[] | null): string | undefined {
  if (!actual || actual.length === 0) return undefined;
  const partes = actual.map((f) => {
    const previo = anterior?.find((p) => p.moneda === f.moneda)?.total ?? 0;
    if (previo <= 0) return `${f.moneda}: sin mes anterior para comparar`;
    const variacion = ((f.total - previo) / previo) * 100;
    const signo = variacion >= 0 ? "+" : "";
    return `${f.moneda} ${signo}${variacion.toFixed(0)}% vs. mes anterior`;
  });
  return partes.join(" · ");
}

// "Una lectura de tendencia general" (spec §4 Estrategia) — reusa la MISMA
// comparación de arriba (moneda principal = primera de la lista) para no
// inventar una segunda métrica de tendencia.
function tendenciaGeneral(actual: FilaMoneda[] | null, anterior: FilaMoneda[] | null): string {
  if (!actual || actual.length === 0) return "Sin datos suficientes este mes.";
  const principal = actual[0];
  const previo = anterior?.find((p) => p.moneda === principal.moneda)?.total ?? 0;
  if (previo <= 0) return "Sin mes anterior para comparar.";
  const variacion = ((principal.total - previo) / previo) * 100;
  if (Math.abs(variacion) < 3) return `Facturación estable (${variacion >= 0 ? "+" : ""}${variacion.toFixed(0)}%).`;
  return variacion > 0
    ? `Facturación en alza (+${variacion.toFixed(0)}% vs. mes anterior).`
    : `Facturación en baja (${variacion.toFixed(0)}% vs. mes anterior).`;
}

// "Carga del equipo — UN indicador agregado tipo semáforo" (spec §2
// Operación) — reusa EXACTAMENTE calcularNivelCarga() (mi-trabajo/colaTrabajo.ts),
// el mismo umbral que ya usa CargaEquipoTabla.tsx, aplicado a cada abogado, y
// se queda con el nivel MÁS SEVERO del equipo (nunca un promedio: para
// "dónde debo intervenir" el peor caso es la señal útil — un promedio lo
// escondería detrás del resto del equipo).
const RANGO_NIVEL_CARGA: Record<NivelCarga, number> = { Sobrecargada: 0, Alta: 1, Media: 2, Baja: 3 };
function nivelEquipoMasSevero(items: CargaTrabajoAbogado[]): NivelCarga | null {
  if (items.length === 0) return null;
  let peor: NivelCarga = "Baja";
  for (const item of items) {
    const total = item.audiencias + item.escritos + item.tareas + item.vencimientos;
    const nivel = calcularNivelCarga(total);
    if (RANGO_NIVEL_CARGA[nivel] < RANGO_NIVEL_CARGA[peor]) peor = nivel;
  }
  return peor;
}

// "Unidad más rentable" (spec §4 Estrategia) — la Unidad con mayor
// `resultado` (ingresos - gastos propios) de GET /dashboard/unidades-negocio,
// el mismo cálculo que ya alimentaba el Comparador de Unidades. Simplificación
// documentada: cuando el resultado de una Unidad tiene más de una moneda, se
// suman los montos sin convertir SÓLO para poder rankear (nunca para
// mostrar: el valor mostrado siempre usa formatearPorMoneda con el desglose
// real por moneda) — el sistema no maneja tipo de cambio en ningún lado, es
// la misma limitación que ya tiene "Ver: Todas" en el resto del Dashboard.
function unidadMasRentable(unidades: UnidadNegocio[]): UnidadNegocio | null {
  if (unidades.length === 0) return null;
  return unidades.reduce((mejor, u) => {
    const valorU = u.resultado.reduce((acc, f) => acc + f.total, 0);
    const valorMejor = mejor.resultado.reduce((acc, f) => acc + f.total, 0);
    return valorU > valorMejor ? u : mejor;
  }, unidades[0]);
}

// Tiles compactos — todos sobre el mismo primitivo `Tarjeta`
// (tamano="compacta") que ya usa TarjetaIndicador en components/Tarjeta.tsx.
// La Vista General es un termómetro de 1 pantalla (spec: 1366×768 sin
// scroll), no admite las tarjetas "grande" (p-6, con ícono + pie de
// tendencia) que sí tienen lugar en las vistas de profundización — por eso
// acá se arma un tile deliberadamente más chico en vez de reusar
// `TarjetaKPI` para cada indicador.

// Operación / Estrategia: etiqueta + un valor corto (texto o chip de color
// — lo último lo necesita el semáforo de Carga del equipo).
function TileCompacta({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <Tarjeta tamano="compacta">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-texto-secundario">{etiqueta}</p>
      <div className="mt-1">{children}</div>
    </Tarjeta>
  );
}

// Negocio: etiqueta + valor + una segunda línea opcional (comparación vs.
// mes anterior) — mismo primitivo compacto, con lugar para esa segunda
// línea sin volver a la altura de TarjetaKPI.
function TileNegocio({ etiqueta, valor, comparacion }: { etiqueta: string; valor: string; comparacion?: string }) {
  return (
    <Tarjeta tamano="compacta">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-texto-secundario">{etiqueta}</p>
      <p className="mt-0.5 truncate text-lg font-semibold text-texto">{valor}</p>
      <p className="truncate text-[11px] text-texto-secundario">{comparacion ?? "Sin comparación"}</p>
    </Tarjeta>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mt-1.5 first:mt-0">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-texto-secundario">{titulo}</h2>
      {children}
    </section>
  );
}

export function DashboardGeneralPage() {
  const { permisos } = useAuth();
  const veTodos = permisos.has("expedientes.ver_todos");
  const veEconomiaPermiso = permisos.has("economia.ver");

  const [datos, setDatos] = useState<{
    ceo: DatosCeo;
    ejecutivo: DatosEjecutivoParcial;
    judicial: DatosJudicialParcial;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargaEquipo, setCargaEquipo] = useState<CargaTrabajoAbogado[] | null>(null);
  const [unidadesNegocio, setUnidadesNegocio] = useState<UnidadNegocio[] | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<DatosCeo>("/dashboard/ceo"),
      api.get<DatosEjecutivoParcial>("/dashboard/ejecutivo"),
      api.get<DatosJudicialParcial>("/dashboard/judicial"),
    ])
      .then(([ceo, ejecutivo, judicial]) => setDatos({ ceo, ejecutivo, judicial }))
      .catch(() => setError("No se pudo cargar el panel."));

    // Carga del equipo: sólo tiene sentido (y sólo el backend lo permite)
    // con expedientes.ver_todos — misma ventana ("esta semana", lunes a
    // domingo) que ya calcula lib/periodo.ts, reusada tal cual.
    if (veTodos) {
      const { desde, hasta } = calcularRangoPeriodo("semana");
      api
        .get<{ items: CargaTrabajoAbogado[] }>(`/mi-trabajo/carga-trabajo?desde=${desde}&hasta=${hasta}`)
        .then((r) => setCargaEquipo(r.items))
        .catch(() => setCargaEquipo(null));
    }

    // Unidad más rentable: sólo con economia.ver (mismo permiso que exige
    // GET /dashboard/unidades-negocio).
    if (veEconomiaPermiso) {
      api
        .get<{ unidades: UnidadNegocio[] }>("/dashboard/unidades-negocio")
        .then((r) => setUnidadesNegocio(r.unidades))
        .catch(() => setUnidadesNegocio(null));
    }
    // veTodos/veEconomiaPermiso SÍ van en las deps a propósito: AuthContext
    // arranca con `permisos` vacío (new Set()) mientras resuelve la sesión,
    // así que en el primer montaje justo después del login estos flags
    // pueden ser `false` todavía. Sin esta dependencia, el efecto corría una
    // sola vez con ese falso negativo y nunca reintentaba — "Carga del
    // equipo"/"Unidad más rentable" quedaban en blanco para siempre en la
    // primera visita real de un usuario, aunque sí tuviera el permiso
    // (confirmado: cambiar de pestaña y volver a General, remontando el
    // componente con `permisos` ya resuelto, sí mostraba el dato correcto).
  }, [veTodos, veEconomiaPermiso]);

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!datos) return <EsqueletoDashboard />;

  const { ceo, ejecutivo, judicial } = datos;
  const veEconomia = ceo.facturacionMes !== null;
  const nivelEquipo = cargaEquipo ? nivelEquipoMasSevero(cargaEquipo) : null;
  const materiaTope = judicial.graficos.porMateria[0] ?? null;
  const unidadTope = unidadesNegocio ? unidadMasRentable(unidadesNegocio) : null;
  const objetivos = listaObjetivos(ejecutivo.objetivos);

  const saldoCajasPorMoneda: FilaMoneda[] = (() => {
    const filas = ejecutivo.finanzas?.saldoCajas ?? [];
    const mapa = new Map<string, number>();
    for (const f of filas) mapa.set(f.moneda, (mapa.get(f.moneda) ?? 0) + f.saldo);
    return [...mapa.entries()].map(([moneda, total]) => ({ moneda, total }));
  })();

  return (
    <div>
      <FranjaEstado alertas={ceo.centroOperaciones.alertasCriticas} />

      <Bloque titulo="Operación">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TileCompacta etiqueta="Expedientes activos">
            <p className="text-xl font-semibold text-texto">{ceo.expedientesActivos.toLocaleString("es-AR")}</p>
          </TileCompacta>
          <TileCompacta etiqueta="Carga del equipo">
            {nivelEquipo ? (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-semibold ${resolverColorSemantico(COLOR_POR_NIVEL_CARGA[nivelEquipo]).chip}`}
              >
                {nivelEquipo}
              </span>
            ) : (
              <p className="text-xl font-semibold text-texto-secundario">—</p>
            )}
          </TileCompacta>
          <TileCompacta etiqueta="Tiempo promedio de resolución">
            <p className="text-xl font-semibold text-texto">
              {judicial.tiempoPromedioResolucionDias != null
                ? `${Math.round(judicial.tiempoPromedioResolucionDias)} días`
                : "—"}
            </p>
          </TileCompacta>
        </div>
      </Bloque>

      {veEconomia && (
        <Bloque titulo="Negocio">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TileNegocio
              etiqueta="Facturación del mes"
              valor={formatearPorMoneda(ceo.facturacionMes)}
              comparacion={compararFacturacion(ceo.facturacionMes, ceo.facturacionMesAnterior)}
            />
            <TileNegocio etiqueta="Resultado del mes" valor={formatearPorMoneda(ceo.resultadoMes)} />
            <TileNegocio etiqueta="Honorarios pendientes" valor={formatearPorMoneda(ceo.honorariosPendientes)} />
            <TileNegocio etiqueta="Saldo de Cajas" valor={formatearPorMoneda(saldoCajasPorMoneda)} />
          </div>
          {/* Objetivos — misma fuente y mismo cálculo de % que PanelObjetivos
              (BarraObjetivo/listaObjetivos reusados tal cual), pero en
              grilla horizontal en vez de barras apiladas: "barras de
              progreso compactas" (spec), no la sección larga de la vista
              Financiero. */}
          {objetivos.length > 0 && (
            <div className="mt-1.5">
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-texto-secundario">Objetivos del mes</p>
              <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 rounded-xl border border-borde bg-superficie p-3 shadow-md sm:grid-cols-2 lg:grid-cols-4">
                {objetivos.map((o) => (
                  <BarraObjetivo key={o.etiqueta} etiqueta={o.etiqueta} actual={o.actual} objetivo={o.objetivo} moneda={o.moneda} />
                ))}
              </div>
            </div>
          )}
        </Bloque>
      )}

      <Bloque titulo="Estrategia">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TileCompacta etiqueta="Materia con mayor volumen">
            <p className="truncate text-sm font-semibold text-texto">
              {materiaTope ? `${materiaTope.etiqueta} (${materiaTope.total})` : "Sin datos"}
            </p>
          </TileCompacta>
          <TileCompacta etiqueta="Unidad más rentable">
            <p className="truncate text-sm font-semibold text-texto">
              {veEconomiaPermiso
                ? unidadTope
                  ? `${unidadTope.nombre} · ${formatearPorMoneda(unidadTope.resultado)}`
                  : "Sin datos"
                : "—"}
            </p>
          </TileCompacta>
          <TileCompacta etiqueta="Tendencia general">
            <p className="truncate text-sm font-semibold text-texto">
              {veEconomia ? tendenciaGeneral(ceo.facturacionMes, ceo.facturacionMesAnterior) : "—"}
            </p>
          </TileCompacta>
        </div>
      </Bloque>
    </div>
  );
}
